import React, { useState, useEffect } from 'react';
import { 
  FormControl, 
  Select, 
  MenuItem, 
  Box,
  Typography
} from '@mui/material';

const SuperPickemSeriesDropdown = ({ 
  bracketState,
  seriesLengths, 
  setSeriesLengths, 
  games, 
  matchupId, 
  picksInfo, 
  round,
  hasBracketDeadlinePassed,
  isThisGamePickIn, 
  checkForCompleteRoundAndScroll,
}) => {
  // Use state for the selected length, initialized from props
  const [selectedLength, setSelectedLength] = useState('');
  const [selectedResult, setSelectedResult] = useState('');
  
  // Update local state when seriesLengths prop changes
  useEffect(() => {
    const existingPick = seriesLengths?.find(
      item => item.matchupId === matchupId && item.round === round
    );
    setSelectedLength(existingPick ? existingPick.seriesLength : '');
    setSelectedResult(existingPick ? existingPick.result : '');
  }, [seriesLengths, matchupId, round]);

  // Find min and max game values for this round from picksInfo
  const roundInfo = picksInfo?.find(info => info.round === round);
  const minGames = roundInfo?.minimumGames || 4;
  const maxGames = roundInfo?.maximumGame || 7;

  // Handle selection change
  const handleChange = (event) => {
    const newLength = event.target.value;
    setSelectedLength(newLength);
    
    // If seriesLengths doesn't exist yet, initialize it as an empty array
    const updatedSeriesLengths = seriesLengths || [];
    
    // Check if this matchup already has a selection
    const existingIndex = updatedSeriesLengths.findIndex(
      item => item.matchupId === matchupId && item.round === round
    );
    
    let newSeriesLengths;
    if (existingIndex >= 0) {
      // Replace existing selection
      newSeriesLengths = [...updatedSeriesLengths];
      newSeriesLengths[existingIndex] = {
        matchupId,
        round,
        seriesLength: newLength
      };
      setSeriesLengths(newSeriesLengths);
    } else {
      // Add new selection
      newSeriesLengths = [
        ...updatedSeriesLengths,
        {
          matchupId,
          round,
          seriesLength: newLength
        }
      ];
      setSeriesLengths(newSeriesLengths);
    }
  
    // Only trigger completion check if this is for the current round
    // and we've made a valid selection
    if (isThisGamePickIn && newLength && checkForCompleteRoundAndScroll) {
      // Get the current round's bracket picks
      const roundBracketPicks = bracketState?.filter(pick => pick.round === round) || [];
      
      // Get the max picks required for this round
      const maxPicksForRound = picksInfo?.find(pick => pick.round === round)?.picks || 0;
      
      // Only proceed if we have the right number of bracket picks for this round
      if (roundBracketPicks.length === maxPicksForRound) {
        // Count how many of these bracket picks have series lengths after this selection
        let completePicksCount = 0;
        
        for (const bracketPick of roundBracketPicks) {
          // Check if this bracket pick has a series length in our updated array
          const hasSeriesLength = newSeriesLengths.some(sl => 
            sl.matchupId === bracketPick.matchupId && sl.round === bracketPick.round
          );
          
          if (hasSeriesLength) {
            completePicksCount++;
          }
        }
        
        // Only trigger scrolling if this selection completes all picks for the round
        if (completePicksCount === maxPicksForRound) {
          checkForCompleteRoundAndScroll(round, newSeriesLengths);
        }
      }
    }
  };

  // Generate menu items for each possible game length
  const generateMenuItems = () => {
    const items = [];
    
    // Add placeholder item
    items.push(
      <MenuItem key="placeholder" value="" disabled>
        Series Length
      </MenuItem>
    );
    
    // Add items from min to max games
    for (let i = minGames; i <= maxGames; i++) {
      items.push(
        <MenuItem key={i} value={i}>
          {i} Games
        </MenuItem>
      );
    }
    
    return items;
  };

  // Common styles for both states
  const commonBoxStyle = {
    minWidth: 160,
    height: '24px',
  };

  const textColor = selectedResult === 'win'
        ? '#00AA72'
          : (selectedResult === 'loss' || selectedLength === '')
            ? '#CC293C'
              : '#002129';

  // If deadline has passed, render a static box instead of a dropdown
  if (hasBracketDeadlinePassed) {
    const displayValue = selectedLength ? `${selectedLength} Games` : 'No Pick';
    
    return (
      <Box 
        sx={{
          ...commonBoxStyle,
          border: '1px solid #002129',
          borderRadius: '4px',
          backgroundColor: 'white',
          color: '#002129',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '14px',
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '14px', 
            color: textColor,
            fontWeight: 700, 
          }}
        >
          {displayValue}
        </Typography>
      </Box>
    );
  }

  // If deadline has not passed, render the dropdown as before
  return (
    <Box sx={commonBoxStyle}>
      <FormControl fullWidth size="small" variant="outlined">
        <Select
          id={`series-length-select-${matchupId}-${round}`}
          value={selectedLength}
          onChange={handleChange}
          displayEmpty
          renderValue={(selected) => {
            return selected === '' ? 'Series Length' : `${selected} Games`;
          }}
          sx={{
            height: '24px',
            backgroundColor: (!hasBracketDeadlinePassed && isThisGamePickIn && !selectedLength) ? 'rgba(204, 41, 60, 0.50)' : 'white',
            color: '#002129',
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: '#002129',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#002129',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#002129',
            },
            '.MuiSelect-select': {
              paddingTop: '0px',
              paddingBottom: '0px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '24px !important',
              fontSize: '14px',
            }
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                '& .MuiMenuItem-root': {
                  color: '#002129',
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#e0e0e0',
                    '&.Mui-focusVisible': { 
                      backgroundColor: '#e0e0e0' 
                    }
                  },
                }
              }
            }
          }}
          inputProps={{
            sx: {
              '&.Mui-focused': {
                backgroundColor: 'white'
              }
            }
          }}
        >
          {generateMenuItems()}
        </Select>
      </FormControl>
    </Box>
  );
};

export default SuperPickemSeriesDropdown;